import type { MaybeRefOrGetter } from 'vue'
import { inject, ref, toValue } from 'vue'
import { Manager, Socket } from 'socket.io-client'
import { WebsocketClientKey } from '~/api/symbols'
import { Notify } from '~/lib/notify'
import useI18nGlobal from '~/composables/common/useI18nGlobal'

export type WSNotification = { type: string; message: string }
export type WSConnectionResponse = {
  status: string
  sid: string
  user: { id: number; email: string; name: string; admin: boolean }
}

export type WSProgressNotification = {
  id: string
  label: string
  step?: string | null
  state: string
  current: number
  total: number
  percent: number
  created_at: string
  completed_at?: string | null
}
/**
 * Needs to be manually maintained, since the socket.io-client does not provide types for events.
 */
type ServerToClientEvents = {
  notification: (data: WSNotification) => void
  progress: (data: WSProgressNotification) => void
  connection_response: (data: WSConnectionResponse) => void // happens when connect succeeded
}

type ClientToServerEvents = {
  ping: (...args: unknown[]) => void
  message: (data: object) => void // Just logs to server
}

export type WebsocketClient = ReturnType<typeof createWebsocketClient>
export function createWebsocketClient(tokenRef: MaybeRefOrGetter<string | null>, socketUrl: URL) {
  const { t } = useI18nGlobal()
  const { host, port, pathname } = socketUrl
  const manager = new Manager({
    path: pathname,
    host: host,
    port: port,
    autoConnect: false, // do later, to provide auth-token,
    // I made some effort to use "websocket".
    // But earlier I had trouble setting up the proxy for websockets and used "polling" (I didn't know how to handle ws upgrade in proxy)
    transports: ['websocket', 'polling'],
    reconnectionDelayMax: 10000,
  })
  manager.on('error', (error) => console.log('[Websocket] Manager error', error))

  const socket: Socket<ServerToClientEvents, ClientToServerEvents> = manager.socket('/')
  const connected = ref(false)
  const connecting = ref<boolean>(false)
  let connectAttempt: Promise<void> | null = null
  let notifyConnectionFailed = true

  // Connected
  socket.on('connect', () => {
    console.info('[Websocket] connected')
    connected.value = true

    if (!notifyConnectionFailed) {
      Notify.success(t('WebsocketReconnected'))
    }
    notifyConnectionFailed = true
  })

  // Connect error
  socket.on('connect_error', (err) => {
    console.error('[Websocket] connection error', err)
    connected.value = false

    if (notifyConnectionFailed) {
      Notify.warning(t('WebsocketConnectFailed', { reason: String(err) }))
      notifyConnectionFailed = false
    }
  })

  // Disconnected
  socket.on('disconnect', (reason) => {
    console.info(`[Websocket] disconnected - ${reason}`)
    connected.value = false
  })

  /**
   * Returns a Promise to wait for the `connect` event to resolve
   */
  async function connect(token?: string): Promise<void> {
    if (connectAttempt) {
      return await connectAttempt
    }
    connectAttempt = new Promise<void>((resolve, reject) => {
      connecting.value = true

      if (socket.connected) {
        return resolve()
      }
      socket.auth = { token: token ?? toValue(tokenRef) }
      socket.once('connect', async () => {
        connected.value = socket.connected
        resolve()
      })
      socket.once('connect_error', reject)
      socket.connect() // this does not throw an err but emits connect_error
    }).finally(() => {
      connecting.value = false
      connectAttempt = null
    })
    return await connectAttempt
  }

  async function toggleConnection() {
    if (connected.value) {
      socket.disconnect()
      return
    }
    try {
      await connect()
    } catch {
      /* empty */
    }
  }

  return {
    connected: connected,
    connecting: connecting,
    connect: connect,
    disconnect: socket.disconnect.bind(socket),
    toggleConnection: toggleConnection,
    emit: socket.emit.bind(socket),
    on: socket.on.bind(socket),
    off: socket.off.bind(socket),
    onAny: socket.onAny.bind(socket),
    offAny: socket.offAny.bind(socket),
  }
}

export function injectWebsocketClient() {
  const ws = inject(WebsocketClientKey)
  if (!ws) {
    throw Error('Websocket client missing')
  }
  return ws
}
